import { useState, useEffect } from 'react';

interface CacheEntry {
  hash: string;
  blob: Blob;
  timestamp: number;
  contentType: string;
}

const CACHE_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
const DB_NAME = 'imageCache';
const STORE_NAME = 'images';
const DB_VERSION = 1;

export const useCachedImages = () => {
  const [db, setDb] = useState<IDBDatabase | null>(null);

  // Initialize IndexedDB
  useEffect(() => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('Failed to open IndexedDB');
    };

    request.onsuccess = (event) => {
      setDb((event.target as IDBOpenDBRequest).result);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'hash' });
      }
    };

    return () => {
      if (db) {
        db.close();
      }
    };
  }, []);

  const saveToCache = async (
    hash: string,
    blob: Blob,
    contentType?: string
  ): Promise<string> => {
    if (!db) return URL.createObjectURL(blob);

    return new Promise((resolve) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);

      // Create a new blob with the specified content type if provided
      const blobToStore = contentType
        ? new Blob([blob], { type: contentType })
        : blob;

      const entry: CacheEntry = {
        hash,
        blob: blobToStore,
        timestamp: Date.now(),
        contentType: contentType || blob.type,
      };

      store.put(entry);
      resolve(URL.createObjectURL(blobToStore));
    });
  };

  const get = async (hash: string, path: string): Promise<string | null> => {
    if (!db) return null;

    try {
      // Check cache
      const cached: CacheEntry = await new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.get(hash);

        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });

      if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        console.log('CACHE HIT for hash:', hash);
        // Create a new blob with the cached content type
        const blob = new Blob([cached.blob], { type: cached.contentType });
        return URL.createObjectURL(blob);
      }

      // Fetch new image if not in cache or expired
      console.log('CACHE MISS for hash:', hash);
      const tokenResponse = await fetch('/access-token');
      const { accessToken } = await tokenResponse.json();

      const response = await fetch(`/api/blob/${path}?redirect=true`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) throw new Error('Failed to fetch image');

      const blob = await response.blob();
      const contentType = path.toLowerCase().endsWith('.pdf')
        ? 'application/pdf'
        : blob.type;

      return saveToCache(hash, blob, contentType);
    } catch (error) {
      console.error('Error fetching image:', error);
      return null;
    }
  };

  // Clean up old cache entries
  useEffect(() => {
    const cleanupCache = async () => {
      if (!db) return;

      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.openCursor();

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          const entry = cursor.value as CacheEntry;
          if (Date.now() - entry.timestamp > CACHE_DURATION) {
            store.delete(cursor.key);
          }
          cursor.continue();
        }
      };
    };

    if (db) {
      cleanupCache();
    }
  }, [db]);

  return { get };
};
